// Types
import { CreateCampaignDeliverableFiles_createCampaignDeliverableFiles } from 'api/campaignV2/types/CreateCampaignDeliverableFiles';
import { GetCampaignsV2_getCampaigns_entities_search } from 'api/campaignV2/types/GetCampaignsV2';
import {
  CampaignOfferStatus,
  CampaignSmPlatform,
  CampaignStatus,
  CampaignType,
} from 'api/graphql-global-types';
import { SettingsFormInputs } from 'components/common/SetupDeals/SetupDeals';
// Helpers
import { platformLabel } from './customCampaignConstants';
import { formatNumber } from './utils';

export const mapCampaignStatusToLabel = (status: CampaignStatus): string => {
  switch (status) {
    case CampaignStatus.DETAIL_FILLED:
      return 'Details filled';
    case CampaignStatus.ON_REVIEW:
      return 'On review';
    case CampaignStatus.DECLINED:
      return 'Declined';
    case CampaignStatus.COMPLETED:
      return 'Completed';
    case CampaignStatus.PAID_AND_ACTIVE:
      return 'Paid and active';
    case CampaignStatus.PAID_AND_BUDGET_FULFILLED:
      return 'Paid and budget fulfilled';
    default:
      return 'N/a';
  }
};

const excludedKeys = [
  'contactEmail',
  'contactPhoneNumberE164',
  'minFlatPrice',
  'minBookMePrice',
  'minSocialPrice',
  'minSocialSharePrice',
  'minSponsoredLivePrice',
  'isArticlesPrEnabled',
  'isAcceptingFreeProducts',
];

export const getDisabledCategories = (values: SettingsFormInputs): string[] => {
  return (Object.keys(values) as (keyof SettingsFormInputs)[]).filter(
    (key) => !excludedKeys.includes(key) && values[key] === false
  ) as string[];
};

export const CAMPAIGN_STATUS_OPTIONS = [
  {
    text: 'Completed',
    value: CampaignStatus.COMPLETED,
  },
  {
    text: 'Declined',
    value: CampaignStatus.DECLINED,
  },
  {
    text: 'Details filled',
    value: CampaignStatus.DETAIL_FILLED,
  },
  {
    text: 'On review',
    value: CampaignStatus.ON_REVIEW,
  },
  {
    text: 'Paid and active',
    value: CampaignStatus.PAID_AND_ACTIVE,
  },
  {
    text: 'Paid and budget fulfilled',
    value: CampaignStatus.PAID_AND_BUDGET_FULFILLED,
  },
];

export const CAMPAIGN_OFFER_STATUS_OPTIONS = [
  {
    text: 'Offer Declined',
    value: CampaignOfferStatus.DECLINED,
  },
  {
    text: 'Offer Sent',
    value: CampaignOfferStatus.PROPOSED,
  },
  {
    text: 'Invited',
    value: CampaignOfferStatus.INVITED,
  },
  {
    text: 'Counter Offer',
    value: CampaignOfferStatus.COUNTER_OFFER_PENDING,
  },
  {
    text: 'Submitted',
    value: CampaignOfferStatus.SUBMITTED,
  },
  {
    text: 'Published',
    value: CampaignOfferStatus.PUBLISHED,
  },
  {
    text: 'Completed',
    value: CampaignOfferStatus.COMPLETED,
  },
  {
    text: 'Approved',
    value: CampaignOfferStatus.ACCEPTED,
  },
  {
    text: 'Applied',
    value: CampaignOfferStatus.APPLIED,
  },
  {
    text: 'Expired',
    value: CampaignOfferStatus.EXPIRED,
  },
  {
    text: 'Scheduled',
    value: CampaignOfferStatus.SCHEDULED,
  },
  {
    text: 'Deliverables rejected',
    value: CampaignOfferStatus.DELIVERABLES_REJECTED,
  },
  {
    text: 'Deliverables approved',
    value: CampaignOfferStatus.DELIVERABLES_APPROVED,
  },
];

export const CAMPAIGN_TYPE_FILTER_OPTIONS = [
  {
    text: 'Book Me',
    value: CampaignType.BookMe,
  },
  {
    text: 'Direct Booking',
    value: CampaignType.DirectBooking,
  },
  {
    text: 'Social',
    value: CampaignType.Social,
  },
  {
    text: 'Social Boost',
    value: CampaignType.SocialBoost,
  },
  {
    text: 'Social Share',
    value: CampaignType.SocialShare,
  },
  {
    text: 'Sponsored Appearance',
    value: CampaignType.SponsoredAppearance,
  },
  {
    text: 'Press Interviews & Event Marketing',
    value: CampaignType.EventMarketing,
  },
  {
    text: 'UGC',
    value: CampaignType.Ugc,
  },
  {
    text: 'Custom Campaign',
    value: CampaignType.Custom,
  },
];

export const getCampaignTypeLabel = (type: CampaignType): string => {
  const campaignTypeToLabel = {
    [CampaignType.BookMe]: 'Book Me',
    [CampaignType.DirectBooking]: 'Direct Booking',
    [CampaignType.Social]: 'Social',
    [CampaignType.SocialBoost]: 'Social Boost',
    [CampaignType.SocialShare]: 'Social Share',
    [CampaignType.SponsoredAppearance]: 'Sponsored Appearance',
    [CampaignType.EventMarketing]: 'Press Interviews & Event Marketing',
    [CampaignType.Ugc]: 'UGC',
    [CampaignType.Custom]: 'Custom Campaign',
  };

  if (type in campaignTypeToLabel) {
    return campaignTypeToLabel[type as keyof typeof campaignTypeToLabel];
  }

  return type;
};

export const SOCIAL_FOLLOWERS_OPTIONS = [
  {
    text: '<10k',
    value: '<10k',
  },
  {
    text: '10k - 100k',
    value: '10k-100k',
  },
  {
    text: '100k - 1m',
    value: '100k-1m',
  },
  {
    text: '1m+',
    value: '1m+',
  },
];

export const mapOfferStatusToLabel = (status: CampaignOfferStatus): string => {
  switch (status) {
    case CampaignOfferStatus.DECLINED:
      return 'Offer Declined';
    case CampaignOfferStatus.PROPOSED:
      return 'Offer Sent';
    case CampaignOfferStatus.INVITED:
      return 'Invited';
    case CampaignOfferStatus.COUNTER_OFFER_PENDING:
      return 'Counter Offer';
    case CampaignOfferStatus.SUBMITTED:
      return 'Submitted';
    case CampaignOfferStatus.ACCEPTED:
      return 'Approved';
    case CampaignOfferStatus.APPLIED:
      return 'Applied';
    case CampaignOfferStatus.EXPIRED:
      return 'Expired';
    case CampaignOfferStatus.SCHEDULED:
      return 'Scheduled';
    case CampaignOfferStatus.PUBLISHED:
      return 'Published';
    case CampaignOfferStatus.DELIVERABLES_APPROVED:
      return 'Deliverables Approved';
    case CampaignOfferStatus.DELIVERABLES_REJECTED:
      return 'Deliverables Declined';
    default:
      return status;
  }
};

interface FollowerRange {
  from: number;
  to: number;
}

const FOLLOWERS_FILTER_RANGES: Record<string, FollowerRange> = {
  '<10k': { from: 0, to: 9_999 },
  '10k-100k': { from: 10_000, to: 100_000 },
  '100k-1m': { from: 100_001, to: 1_000_000 },
  '1m+': { from: 1_000_000, to: 100_000_000 },
};

export const getFollowerRange = (
  selectedFilters: string
): { from: number; to: number } | null => {
  if (!selectedFilters?.length) return null;

  let from = Infinity;
  let to = 0;

  const { from: filterMin, to: filterMax } =
    FOLLOWERS_FILTER_RANGES[selectedFilters];

  from = Math.min(from, filterMin);
  to = Math.max(to, filterMax);

  return { from, to };
};

export type UploadFileType = File & {
  preview?: string;
  path?: string;
};

type FileToUpload = {
  fields: string;
  url: string;
  key: string;
  type: string;
  file: File;
  name: string;
};

export const uploadDeliverables = async (
  preSignedUrls: CreateCampaignDeliverableFiles_createCampaignDeliverableFiles[],
  files?: FileWithPreview[] | File[]
): Promise<FileToUpload[]> => {
  if (!files) return [];

  const res: FileToUpload[] = [];

  files.forEach((file, index) => {
    const preSignedUrl = preSignedUrls?.[index];

    res.push({
      fields: preSignedUrl?.fields || '',
      url: preSignedUrl?.url || '',
      name: file.name,
      type: file.type,
      file: file,
      key: preSignedUrl?.key,
    });
  });

  for (const item of res) {
    const { fields, url, file, key, type, name } = item;
    const formData = new FormData();

    Object.entries(JSON.parse(fields)).forEach(([key, value]) => {
      formData.append(key, value as string);
    });

    formData.append('key', key);
    formData.append('file', file, name);
    formData.append('Content-Type', type);

    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      console.error('Upload failed:', await response.text());
      throw new Error(`Upload failed for file ${name}`);
    }
  }
  return res;
};

export enum PLACE_PRIMARY_TYPES {
  Locality = 'locality',
  AdministrativeAreaLevel1 = 'administrative_area_level_1',
  Country = 'country',
}

export const ENGAGEMENT_RATE_OPTIONS = [
  { value: 0.0025, label: '0.25%+' },
  { value: 0.005, label: '0.50%+' },
  { value: 0.0075, label: '0.75%+' },
  { value: 0.01, label: '1.00%+' },
  { value: 0.02, label: '2.00%+' },
  { value: 0.03, label: '3.00%+' },
  { value: 0.04, label: '4.00%+' },
  { value: 0.05, label: '5.00%+' },
  { value: 0.06, label: '6.00%+' },
  { value: 0.07, label: '7.00%+' },
  { value: 0.08, label: '8.00%+' },
  { value: 0.09, label: '9.00%+' },
  { value: 0.1, label: '10.00%+' },
  { value: 0.15, label: '15.00%+' },
  { value: 0.2, label: '20.00%+' },
  { value: 0.25, label: '25.00%+' },
  { value: 0.3, label: '30.00%+' },
  { value: 0.35, label: '35.00%+' },
  { value: 0.4, label: '40.00%+' },
  { value: 0.45, label: '45.00%+' },
  { value: 0.5, label: '50.00%+' },
  { value: 0.6, label: '60.00%+' },
  { value: 0.7, label: '70.00%+' },
  { value: 0.8, label: '80.00%+' },
  { value: 0.9, label: '90.00%+' },
  { value: 1.0, label: '100.00%+' },
];

/**
 * Converts engagement rate from value to select option format
 * @param engagementRateFrom - Engagement rate value (can be null or undefined)
 * @returns Select option object with value and label, or null if value is null/undefined
 */
export const convertEngagementRateToOption = (
  engagementRateFrom: number | null | undefined
): { value: number; label: string } | null => {
  if (engagementRateFrom === null || engagementRateFrom === undefined) {
    return null;
  }
  const value =
    engagementRateFrom === 0
      ? ENGAGEMENT_RATE_OPTIONS[0].value
      : engagementRateFrom;
  const label =
    ENGAGEMENT_RATE_OPTIONS.find((option) => option.value === value)?.label ||
    '';
  return { value, label };
};

/**
 * Extracts engagement rate value from form field (handles both number and object formats)
 * @param engagementRate - Engagement rate from form (can be number, object with value property, null, or undefined)
 * @returns Engagement rate value as number, or null if invalid
 */
export const getEngagementRateValue = (
  engagementRate: number | { value: number; label?: string } | null | undefined
): number | null => {
  if (engagementRate === null || engagementRate === undefined) {
    return null;
  }
  const value =
    typeof engagementRate === 'number' ? engagementRate : engagementRate.value;
  return value !== null && value !== undefined ? value : null;
};

/**
 * Converts days to weeks with proper formatting
 * @param days - Number of days
 * @returns Formatted string showing weeks (exact if whole number, approximate if not)
 */
export const formatDaysToWeeks = (days: number): string => {
  const weeks = days / 7;
  const isExactWeek = weeks === Math.floor(weeks);

  if (isExactWeek) {
    return `${weeks} week${weeks !== 1 ? 's' : ''}`;
  } else {
    return `~${Math.ceil(weeks)} week${Math.ceil(weeks) !== 1 ? 's' : ''}`;
  }
};

export const isOnlyContentDeliverables = (
  campaignType?: CampaignType
): boolean => {
  if (!campaignType) return false;

  return campaignType === CampaignType.Ugc;
};

export const isCustomLikeCampaign = (
  campaignType?: CampaignType | null
): boolean => {
  return (
    campaignType === CampaignType.Custom ||
    campaignType === CampaignType.DirectBooking
  );
};

// Mirrors fs-backend: BookMe and DirectBooking talent is managed by their own flows
const TALENT_REMOVABLE_CAMPAIGN_TYPES = [
  CampaignType.Social,
  CampaignType.SocialShare,
  CampaignType.SponsoredAppearance,
  CampaignType.Ugc,
  CampaignType.Custom,
  CampaignType.SocialBoost,
];

// Mirrors fs-backend: talent can be removed only before they applied
const TALENT_REMOVABLE_OFFER_STATUSES = [
  CampaignOfferStatus.PROPOSED,
  CampaignOfferStatus.INVITED,
  CampaignOfferStatus.COUNTER_OFFER_PENDING,
];

export const canRemoveTalentFromCampaign = (
  campaignType: CampaignType,
  offerStatus: CampaignOfferStatus
): boolean =>
  TALENT_REMOVABLE_CAMPAIGN_TYPES.includes(campaignType) &&
  TALENT_REMOVABLE_OFFER_STATUSES.includes(offerStatus);

export type PlatformFollowerCriteria = {
  id?: string | null;
  platform: CampaignSmPlatform;
  minFollowers: number | null;
  maxFollowers: number | null;
  minEngagementRate?: number | null;
};

/** Mirrors the backend DIGITAL_AGREEMENT_CAMPAIGN_TYPES: types that support the digital agreement */
export const DIGITAL_AGREEMENT_CAMPAIGN_TYPES: CampaignType[] = [
  CampaignType.Custom,
  CampaignType.DirectBooking,
];

/** Talents sign the campaign's agreement, so it needs a brand signatory before approval */
export const requiresAgreementSignatory = (campaign: {
  type: CampaignType;
  requiresAgreement: boolean;
}): boolean =>
  DIGITAL_AGREEMENT_CAMPAIGN_TYPES.includes(campaign.type) &&
  campaign.requiresAgreement;

export const formatFollowerCount = (count: number | null | undefined): string =>
  count != null ? formatNumber(count) : 'Not set';

export const getPlatformCriteria = (
  search: GetCampaignsV2_getCampaigns_entities_search | null | undefined
): PlatformFollowerCriteria[] => {
  if (
    search?.__typename === 'CustomSearchCriteria' ||
    search?.__typename === 'SocialBoostSearchCriteria'
  ) {
    return search.platformCriteria ?? [];
  }

  return [];
};

export const usesExplicitFollowerBounds = (
  campaignType?: CampaignType | null
): boolean =>
  campaignType === CampaignType.Custom ||
  campaignType === CampaignType.DirectBooking ||
  campaignType === CampaignType.SocialBoost;

export const hasFollowerBounds = (
  minFollowers?: number | null,
  maxFollowers?: number | null
): boolean => minFollowers != null || maxFollowers != null;

export const hasPlatformFollowerBounds = (
  platformCriteria: PlatformFollowerCriteria[]
): boolean =>
  platformCriteria.some((pc) =>
    hasFollowerBounds(pc.minFollowers, pc.maxFollowers)
  );

export const hasRootFollowerBounds = (
  search: GetCampaignsV2_getCampaigns_entities_search | null | undefined,
  campaignType?: CampaignType | null,
  platformHasBounds?: boolean
): boolean =>
  usesExplicitFollowerBounds(campaignType) &&
  !platformHasBounds &&
  hasFollowerBounds(search?.minFollowers, search?.maxFollowers);

export const getFollowerBoundLabel = (
  bound: 'minimum' | 'maximum',
  platform?: CampaignSmPlatform | null
): string => {
  const base = bound === 'minimum' ? 'Follower minimum' : 'Follower maximum';
  return platform ? `${base} (${platformLabel(platform)})` : base;
};

export const isImageType = (file: File): boolean => {
  return file.type.startsWith('image/');
};
